import React, { useState, useEffect } from 'react';
import { Button } from '../../../Components/ui/button';
import { Input } from '../../../Components/ui/input';
import { Badge } from '../../../Components/ui/badge';
import { AlertTriangle } from 'lucide-react';
import CreateContentForm from '../AdminTemplates/CreateContentForm';
import {
  CalendarIcon,
  FileIcon,
  CaretSortIcon,
  DotsHorizontalIcon,
} from '@radix-ui/react-icons';
import { format } from 'date-fns';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../Components/ui/popover';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../Components/ui/table';
import { Calendar } from '../../../Components/ui/calendar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../Components/ui/dropdown-menu';
import { DateRange } from 'react-day-picker';
import { useSelector } from 'react-redux';
import { RootState } from '../../../State/store';
import { useToast } from '../../../Components/ui/use-toast';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../../Components/ui/tooltip';
import * as XLSX from 'xlsx';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../../Components/ui/dialog';

interface Template {
  template_Id: number;
  templateName: string;
  operator: string | null;
  channelType: number;
  templateStatus: string;
  metaStatus?: string;
  createdDate: string;
  lastUpdatedDate: string;
  partnerId: number | null;
  isUsedAsWm: boolean; // ← ADD THIS
}

interface LocationState {
  template_id?: number;
  mode?: 'create' | 'edit' | 'view';
  workspace_id?: number;
  authenticated?: boolean;
}

const ChannelBadge: React.FC<{ channel: string }> = ({ channel }) => {
  return (
    <Badge
      style={
        channel === 'WhatsApp'
          ? { backgroundColor: '#479E98', color: '#fff' }
          : channel === 'SMS'
            ? { backgroundColor: '#DFA548', color: '#fff' }
            : { backgroundColor: '#94A3B8', color: '#fff' }
      }
    >
      {channel}
    </Badge>
  );
};

const TemplateStatusBadge: React.FC<{ value?: string }> = ({ value }) => {
  const raw = (value ?? '').trim();
  const norm = raw.toLowerCase();

  const isApproved = norm === 'approved';
  const isRejected = norm === 'rejected';

  return (
    <span
      className={
        isApproved
          ? '!bg-green-700 text-white hover:!bg-green-700 hover:text-white'
          : isRejected
            ? '!bg-red-600 text-white hover:!bg-red-600 hover:text-white'
            : '!bg-gray-400 text-white hover:!bg-gray-400 hover:text-white'
      }
      style={{
        borderRadius: '9999px',
        padding: '4px 14px',
        fontSize: '12px',
        fontWeight: 500,
        display: 'inline-block',
      }}
    >
      {isApproved ? 'Approved' : isRejected ? 'Rejected' : 'Pending'}
    </span>
  );
};

const DatePickerWithRange: React.FC<{
  dateRange: DateRange | undefined;
  setDateRange: React.Dispatch<React.SetStateAction<DateRange | undefined>>;
}> = ({ dateRange, setDateRange }) => {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant='outline'
          className='min-w-[150px] w-auto justify-start text-left font-normal mt-0'
        >
          <CalendarIcon className='mr-2 h-4 w-4' />
          {dateRange?.from ? (
            dateRange.to ? (
              <>
                {format(dateRange.from, 'LLL dd, y')} -{' '}
                {format(dateRange.to, 'LLL dd, y')}
              </>
            ) : (
              format(dateRange.from, 'LLL dd, y')
            )
          ) : (
            <span>Pick a date</span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className='w-auto p-0' align='start'>
        <Calendar
          initialFocus
          mode='range'
          defaultMonth={dateRange?.from ?? new Date()}
          selected={dateRange}
          onSelect={setDateRange}
          numberOfMonths={1}
        />
      </PopoverContent>
    </Popover>
  );
};

const AdminTemplateList: React.FC = () => {
  const [dateRange, setDateRange] = useState<DateRange | undefined>();
  const [templateList, setTemplateList] = useState<Template[]>([]);
  const [loading, setLoading] = useState(false);
  const [openMenuRowId, setOpenMenuRowId] = useState<number | null>(null);
  const [checkboxSelectedRows, setCheckboxSelectedRows] = useState<number[]>(
    [],
  );
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [totalCount, setTotalCount] = useState<number>(0);
  const adminUrl = useSelector(
    (state: RootState) => state.authentication.adminUrl,
  );
  const [selectedTemplate, setSelectedTemplate] = useState<any>(null);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit' | 'view'>(
    'create',
  );
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const toastHook = useToast();
  const navigate = useNavigate();
  const accountId = useSelector(
    (state: RootState) => state.authentication.account_id,
  );
  const workspaceId = useSelector(
    (state: RootState) => state.authentication.workspace_id,
  );

  const [isDeleted, setIsDeleted] = useState(false);

  const location = useLocation();

  const locationState = location.state as LocationState | null;

  const [isExporting, setIsExporting] = useState(false);

  const [showDeletedTemplateMessage, setShowDeletedTemplateMessage] =
    useState(false);

  useEffect(() => {
    if (!locationState?.template_id) return;

    if (locationState.mode === 'edit' || locationState.mode === 'view') {
      const openTemplate = async () => {
        try {
          const isDeleted = await handleCheckWelcomeTemplateDeletedStatus();

          if (isDeleted) {
            setShowDeletedTemplateMessage(true);
            setIsDialogOpen(true);
            return;
          }

          setShowDeletedTemplateMessage(false);
          setDialogMode(locationState.mode!);
          setIsDialogOpen(true);
          setSelectedTemplate(null);

          const response = await axios.get(`${adminUrl}/GetTemplateById`, {
            params: {
              templateId: locationState.template_id,
            },
          });

          if (response.data) {
            setSelectedTemplate(response.data);
          } else {
            setIsDialogOpen(false);
          }
        } catch (error) {
          console.error('Auto-open edit failed:', error);
          setIsDialogOpen(false);
        }
      };

      openTemplate();

      // Clear navigation state
      window.history.replaceState({}, document.title);
    }

    if (locationState.mode === 'create') {
      setDialogMode('create');
      setSelectedTemplate(null);
      setIsDialogOpen(true);
    }
  }, []);
  // Debounce search term
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  // Main effect - triggers API call when dependencies change
  useEffect(() => {
    // Only proceed if search is not being debounced
    if (searchTerm !== debouncedSearchTerm) return;

    let startDate: string | undefined;
    let endDate: string | undefined;

    const bothDatesSelected = dateRange?.from && dateRange?.to;
    const bothDatesCleared = !dateRange?.from && !dateRange?.to;
    const singleDateSelected = dateRange?.from && !dateRange?.to;

    if (bothDatesSelected) {
      startDate = format(dateRange.from!, 'yyyy-MM-dd');
      endDate = format(dateRange.to!, 'yyyy-MM-dd');
    }

    // Only call API if dates are valid (both selected or both cleared, but not single date)
    if ((bothDatesSelected || bothDatesCleared) && !singleDateSelected) {
      fetchTemplates({
        page: currentPage,
        rowsPerPage,
        searchTerm: debouncedSearchTerm,
        startDate,
        endDate,
      });
    } else if (!dateRange?.from && !dateRange?.to) {
      // Initial load with no dates
      fetchTemplates({
        page: currentPage,
        rowsPerPage,
        searchTerm: debouncedSearchTerm,
        startDate: undefined,
        endDate: undefined,
      });
    }
  }, [
    currentPage,
    rowsPerPage,
    debouncedSearchTerm,
    dateRange?.from,
    dateRange?.to,
  ]);

  useEffect(() => {
    setTimeout(() => {
      document.body.style.pointerEvents = '';
    }, 500);
  }, [isDialogOpen]);

  const [deleteConfirm, setDeleteConfirm] = useState<{
    open: boolean;
    templateId: number | null;
  }>({ open: false, templateId: null });

  const handleDelete = async (template_Id: number) => {
    try {
      const response = await axios.get(`${adminUrl}/DeleteWelcomeTemplate`, {
        params: {
          template_id: template_Id,
          accountId: accountId,
          workspaceId: workspaceId,
        },
      });

      if (response.data.status === 'Success') {
        toastHook.toast({
          title: 'Success',
          description: 'Template submitted successfully',
        });

        fetchTemplates({
          page: currentPage,
          rowsPerPage,
          searchTerm: debouncedSearchTerm,
        });
      } else {
        toastHook.toast({
          title: 'Error',
          description:
            response.data.status_description || 'Failed to delete template',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error deleting template:', error);
      toastHook.toast({
        title: 'Error',
        description: 'Failed to delete template',
        variant: 'destructive',
      });
    } finally {
      setDeleteConfirm({ open: false, templateId: null });
    }
  };

  const handleCheckWelcomeTemplateDeletedStatus = async () => {
    try {
      const response = await axios.get(
        `${adminUrl}/GetWelcomeTemplateDeletedStatus`,
        {
          params: {
            templateId: locationState?.template_id,
          },
        },
      );

      const isDeleted = response.data?.isDeleted ?? false;

      setIsDeleted(isDeleted);

      return isDeleted;
    } catch (error) {
      console.error('Error checking welcome template deleted status:', error);

      setIsDeleted(false);

      return false;
    }
  };

  const fetchTemplates = async (params: {
    page: number;
    rowsPerPage: number;
    searchTerm: string;
    startDate?: string;
    endDate?: string;
  }) => {
    setLoading(true);
    try {
      const apiParams: any = {
        page: params.page,
        rowsPerPage: params.rowsPerPage,
      };

      if (params.searchTerm) apiParams.searchTerm = params.searchTerm;
      if (params.startDate) apiParams.startDate = params.startDate;
      if (params.endDate) apiParams.endDate = params.endDate;

      console.log('🔥 API CALL - Params:', apiParams);

      const response = await axios.get(`${adminUrl}/GetTemplatesByChannel`, {
        params: apiParams,
      });

      if (response.data.status === 'Success') {
        // Map the API response to match component's interface
        const mappedData = response.data.data.map((item: any) => ({
          template_Id: item.templateId,
          templateName: item.templateName,
          operator: item.operatorName,
          channelType: item.channelType,
          templateStatus: item.templateStatus,
          createdDate: item.createdDate,
          lastUpdatedDate: item.lastUpdatedDate,
          partnerId: item.partnerId,
          isUsedAsWm: item.isUsedAsWm,
        }));

        setTemplateList(mappedData);
        setTotalCount(response.data.totalCount);
      } else {
        setTemplateList([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
      setTemplateList([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const getChannelName = (channelType: number): string => {
    switch (channelType) {
      case 1:
        return 'WhatsApp';
      case 2:
        return 'SMS';
      default:
        return 'Unknown';
    }
  };

  const handleSelectAll = () => {
    if (isAllSelected) {
      setCheckboxSelectedRows([]);
    } else {
      setCheckboxSelectedRows(templateList.map((_, index) => index));
    }
    setIsAllSelected(!isAllSelected);
  };

  const handleCheckboxRowSelect = (index: number) => {
    setCheckboxSelectedRows((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index],
    );
  };

  useEffect(() => {
    setIsAllSelected(
      templateList.length > 0 &&
        checkboxSelectedRows.length === templateList.length,
    );
  }, [checkboxSelectedRows, templateList]);

  const handlePageChange = (newPage: number) => {
    const totalPages = Math.ceil(totalCount / rowsPerPage);
    if (newPage > 0 && newPage <= totalPages) {
      console.log('🔥 PAGE CHANGE:', newPage); // Debug log
      setCurrentPage(newPage);
    }
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    console.log('🔥 ROWS PER PAGE CHANGE:', newRowsPerPage); // Debug log
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(1); // Reset to first page
  };

  const handleExportButtonClick = async () => {
    setIsExporting(true);
    try {
      const apiParams: any = {};
      if (debouncedSearchTerm) apiParams.searchTerm = debouncedSearchTerm;
      if (dateRange?.from)
        apiParams.startDate = format(dateRange.from, 'yyyy-MM-dd');
      if (dateRange?.to) apiParams.endDate = format(dateRange.to, 'yyyy-MM-dd');

      const response = await axios.get(
        `${adminUrl}/GetTemplatesByChannelWithOutPagination`,
        { params: apiParams },
      );

      if (response.data.status !== 'Success') {
        return;
      }

      const allTemplates: Template[] = (response.data.data || []).map(
        (item: any) => ({
          template_Id: item.templateId,
          templateName: item.templateName,
          operator: item.operatorName,
          channelType: item.channelType,
          templateStatus: item.templateStatus,
          createdDate: item.createdDate,
          lastUpdatedDate: item.lastUpdatedDate,
          partnerId: item.partnerId,
          isUsedAsWm: item.isUsedAsWm,
        }),
      );

      const filteredData = allTemplates.map(
        ({
          template_Id,
          templateName,
          operator,
          channelType,
          templateStatus,
          createdDate,
          lastUpdatedDate,
          isUsedAsWm,
        }) => ({
          'Template ID': template_Id,
          'Template Name': templateName,
          Operator: operator || 'N/A',
          Channel: getChannelName(channelType),
          Status: getTemplateStatus({
            template_Id,
            templateName,
            operator,
            channelType,
            templateStatus,
            createdDate,
            lastUpdatedDate,
            partnerId: null,
            isUsedAsWm,
          }).status,
          'Created Date': createdDate
            ? format(new Date(createdDate), 'dd/MM/yyyy')
            : 'N/A',
          'Last Modified': lastUpdatedDate
            ? format(new Date(lastUpdatedDate), 'dd/MM/yyyy')
            : 'N/A',
          // 'Used as Welcome Template in Campaign': isUsedAsWm ? 'Yes' : 'No',
        }),
      );

      const worksheet = XLSX.utils.json_to_sheet(filteredData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Admin_Template_List');
      XLSX.writeFile(workbook, 'AdminTemplateList.xlsx');
    } catch (error) {
      console.error('Error exporting templates:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const handleSaveTemplate = async () => {
    try {
      await fetchTemplates({
        page: currentPage,
        rowsPerPage,
        searchTerm: debouncedSearchTerm,
        startDate: dateRange?.from
          ? format(dateRange.from, 'yyyy-MM-dd')
          : undefined,
        endDate: dateRange?.to ? format(dateRange.to, 'yyyy-MM-dd') : undefined,
      });
      setIsDialogOpen(false);

      toastHook.toast({
        title: 'Success',
        description: 'Template submitted successfully',
      });
    } catch (error) {
      console.error('Refresh failed:', error);

      toastHook.toast({
        title: 'Error',
        description: 'Template saved but failed to refresh list',
        variant: 'destructive',
      });
    }
  };

  const handleView = async (template_Id: number) => {
    if (!template_Id) {
      toastHook.toast({
        title: 'Error',
        description: 'Template ID not found',
        variant: 'destructive',
      });
      return;
    }

    setIsDialogOpen(true);
    setDialogMode('view');
    setSelectedTemplate(null);

    try {
      const response = await axios.get(
        `${adminUrl}/GetTemplateById?templateId=${template_Id}`,
      );

      if (response.data) {
        setSelectedTemplate(response.data);
      } else {
        toastHook.toast({
          title: 'Error',
          description: 'Failed to fetch template',
          variant: 'destructive',
        });
        setIsDialogOpen(false);
      }
    } catch (error) {
      console.error('Error fetching template for view:', error);
      toastHook.toast({
        title: 'Error',
        description: 'Failed to fetch template details',
        variant: 'destructive',
      });
      setIsDialogOpen(false);
    }
  };

  const handleEdit = async (template_Id: number) => {
    if (!template_Id) {
      toastHook.toast({
        title: 'Error',
        description: 'Template ID not found',
        variant: 'destructive',
      });
      return;
    }

    setIsDialogOpen(true);
    setDialogMode('edit');
    setSelectedTemplate(null);

    try {
      const response = await axios.get(
        `${adminUrl}/GetTemplateById?templateId=${template_Id}`,
      );

      if (response.data) {
        setSelectedTemplate(response.data);
      } else {
        toastHook.toast({
          title: 'Error',
          description: 'Failed to fetch template',
          variant: 'destructive',
        });
        setIsDialogOpen(false);
      }
    } catch (error) {
      console.error('Error fetching template for edit:', error);
      toastHook.toast({
        title: 'Error',
        description: 'Failed to fetch template details',
        variant: 'destructive',
      });
      setIsDialogOpen(false);
    }
  };

  const totalPages = Math.ceil(totalCount / rowsPerPage);

  const handleTemplateStatusClick = (
    templateId: number,
    channelType: number,
  ) => {
    navigate('/adminNavbar/templatestatus', {
      state: {
        campaignId: templateId,
        channel_type: channelType,
      },
    });
  };

  const getTemplateStatus = (template: Template) => {
    const templateStatus = template.templateStatus.toLowerCase();
    const channelType = template.channelType;

    // Check for rejection (any channel)
    if (templateStatus.includes('rejected')) {
      return { status: 'Rejected', showAction: true, variant: 'rejected' };
    }

    // WhatsApp (channelType = 1): Show "Approved" if status is "Whatsapp Approved"
    if (channelType === 1 && templateStatus === 'whatsapp approved') {
      return { status: 'Approved', showAction: false, variant: 'approved' };
    }

    // SMS (channelType = 2): Show "Approved" if status is "Operator Approved"
    if (channelType === 2 && templateStatus === 'operator approved') {
      return { status: 'Approved', showAction: false, variant: 'approved' };
    }

    // Default: Show the original status as Pending
    return { status: 'Pending', showAction: false, variant: 'pending' };
  };

  return (
    <TooltipProvider>
      <div>
        <div className='fixed flex justify-end items-end right-0 top-[-24px] z-20 p-4 mr-2'>
          <Button
            className='w-30 text-sm text-[#F8FAFC] font-medium h-[35px]'
            onClick={() => {
              setDialogMode('create');
              setSelectedTemplate(null);
              setIsDialogOpen(true);
            }}
          >
            Create Template
          </Button>
        </div>

        <div className='mt-4'>
          <div className='flex flex-wrap items-center justify-between gap-4 -mt-6'>
            <div className='flex flex-wrap justify-start w-full md:w-auto'>
              <Input
                placeholder='Search template by name...'
                className='w-full md:w-[350px] text-[14px] font-normal text-[#171717] !mt-0'
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
            <div className='flex flex-wrap items-center justify-end gap-10 w-full md:w-auto'>
              <div className='flex-shrink-0 -mr-5'>
                <DatePickerWithRange
                  dateRange={dateRange}
                  setDateRange={setDateRange}
                />
              </div>
              <div className='flex-shrink-0'>
                <Button
                  variant='outline'
                  className='w-24 text-[#020617] !mt-0'
                  onClick={handleExportButtonClick}
                  disabled={isExporting}
                >
                  {isExporting ? (
                    'Exporting...'
                  ) : (
                    <>
                      <FileIcon
                        style={{ width: '14px', height: '14px' }}
                        className='mr-1 font-bold stroke-[2]'
                      />{' '}
                      Export
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>

          <div className='rounded-md border mt-8'>
            <div className='max-h-[60vh] overflow-y-auto'>
              <Table
                className='rounded-xl whitespace-nowrap border-gray-200'
                style={{ color: '#020202', fontSize: '15px' }}
              >
                <TableHeader>
                  <TableRow className='sticky top-0 z-10 bg-white hover:bg-white'>
                    <TableHead className='pl-6 pr-3 py-2 font-medium align-middle'>
                      <div className='flex items-center gap-3'>
                        <input
                          type='checkbox'
                          className='accent-gray-700'
                          checked={isAllSelected}
                          onChange={handleSelectAll}
                        />
                        <span>Template Name</span>
                      </div>
                    </TableHead>
                    <TableHead className='py-2 px-3 font-medium align-middle'>
                      Operator
                    </TableHead>
                    <TableHead className='py-2 px-3 font-medium align-middle'>
                      Channel
                    </TableHead>
                    <TableHead className='py-2 px-3 font-medium align-middle'>
                      Template Status
                    </TableHead>
                    <TableHead className='py-2 px-3 font-medium align-middle'>
                      Created Date
                    </TableHead>
                    <TableHead className='py-2 px-3 font-medium align-middle'>
                      Last Modified
                    </TableHead>
                    <TableHead className='w-12 py-2 align-middle'></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell
                        colSpan={7}
                        className='text-center py-2 align-middle'
                      >
                        Loading templates...
                      </TableCell>
                    </TableRow>
                  ) : templateList.length > 0 ? (
                    templateList.map((template, index) => {
                      const isSelected = checkboxSelectedRows.includes(index);
                      const statusInfo = getTemplateStatus(template);
                      const canEdit = template.templateStatus
                        .toLowerCase()
                        .includes('rejected');

                      return (
                        <TableRow
                          key={template.template_Id}
                          className='hover:bg-gray-50'
                        >
                          <TableCell className='pl-6 pr-3 py-2 align-middle'>
                            <div className='flex items-center gap-3'>
                              <input
                                type='checkbox'
                                className='accent-gray-700'
                                checked={isSelected}
                                onChange={() => handleCheckboxRowSelect(index)}
                              />
                              <span>{template.templateName}</span>
                            </div>
                          </TableCell>
                          <TableCell className='py-2 px-3 align-middle text-left'>
                            {template.operator || 'N/A'}
                          </TableCell>
                          <TableCell className='py-2 px-3 align-middle text-left'>
                            <ChannelBadge
                              channel={getChannelName(template.channelType)}
                            />
                          </TableCell>
                          <TableCell
                            className='py-2 px-3 align-middle text-left cursor-pointer hover:bg-gray-50'
                            onClick={() =>
                              handleTemplateStatusClick(
                                template.template_Id,
                                template.channelType,
                              )
                            }
                          >
                            <div className='flex items-center gap-2'>
                              <TemplateStatusBadge value={statusInfo.status} />
                              {statusInfo.showAction && (
                                <Tooltip>
                                  <TooltipTrigger>
                                    <AlertTriangle
                                      size={20}
                                      className='text-red-600 transform-gpu transition-transform duration-200 ease-out hover:scale-[1.08]'
                                      aria-hidden='true'
                                    />
                                  </TooltipTrigger>
                                  <TooltipContent className='bg-black text-white border-black'>
                                    <p>Action required: Template rejected</p>
                                  </TooltipContent>
                                </Tooltip>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className='py-2 px-3 align-middle text-left'>
                            {template.createdDate
                              ? format(
                                  new Date(template.createdDate),
                                  'dd/MM/yyyy',
                                )
                              : 'N/A'}
                          </TableCell>
                          <TableCell className='py-2 px-3 align-middle text-left'>
                            {template.lastUpdatedDate
                              ? format(
                                  new Date(template.lastUpdatedDate),
                                  'dd/MM/yyyy',
                                )
                              : 'N/A'}
                          </TableCell>
                          <TableCell className='py-2 pr-3 align-middle'>
                            <DropdownMenu
                              open={openMenuRowId === index}
                              onOpenChange={(open) =>
                                setOpenMenuRowId(open ? index : null)
                              }
                            >
                              <DropdownMenuTrigger asChild>
                                <Button variant='ghost' className='h-8 w-8 p-0'>
                                  <DotsHorizontalIcon className='h-4 w-4' />
                                </Button>
                              </DropdownMenuTrigger>
                              {openMenuRowId === index && (
                                <DropdownMenuContent
                                  align='end'
                                  className='w-40 bg-white shadow-lg rounded-md'
                                >
                                  {canEdit && (
                                    <DropdownMenuItem
                                      onClick={() =>
                                        handleEdit(template.template_Id)
                                      }
                                    >
                                      Edit
                                    </DropdownMenuItem>
                                  )}
                                  <DropdownMenuItem
                                    onSelect={() => {
                                      handleView(template.template_Id);
                                    }}
                                  >
                                    View
                                  </DropdownMenuItem>
                                  {!template.isUsedAsWm && (
                                    <DropdownMenuItem
                                      onClick={() => {
                                        setOpenMenuRowId(null);
                                        setDeleteConfirm({
                                          open: true,
                                          templateId: template.template_Id,
                                        });
                                      }}
                                      className='px-3 py-2 hover:bg-gray-100 cursor-pointer text-red-600'
                                    >
                                      Delete
                                    </DropdownMenuItem>
                                  )}
                                </DropdownMenuContent>
                              )}
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={7}
                        className='text-center py-2 text-gray-500 font-medium align-middle'
                      >
                        {searchTerm.trim() !== ''
                          ? 'No templates found for the searched item.'
                          : 'No templates available.'}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </div>

          <div className='flex justify-between items-center mt-4'>
            <div className='flex items-center space-x-2 text-gray-500 text-sm'>
              <span>{`${(currentPage - 1) * rowsPerPage + 1}-${Math.min(currentPage * rowsPerPage, totalCount)} of ${totalCount} row(s)`}</span>
            </div>
            <div className='flex items-center space-x-4 font-medium text-sm'>
              <span className='text-[#020617] font-medium text-[14px]'>
                Rows per page
              </span>
              <div className='relative inline-block ml-2'>
                <select
                  disabled={templateList.length === 0}
                  className={`cursor-pointer border border-gray-300 rounded-md px-2 py-1 appearance-none focus:outline-none focus:ring-1 focus:ring-blue-500 ${templateList.length === 0 ? 'cursor-not-allowed bg-gray-100' : ''}`}
                  style={{
                    width: '60px',
                    height: '30px',
                    textAlign: 'left',
                    fontSize: '12px',
                    fontWeight: '400',
                    borderColor: '#E5E7EB',
                    boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.1)',
                  }}
                  value={templateList.length === 0 ? 0 : rowsPerPage}
                  onChange={(e) =>
                    handleRowsPerPageChange(Number(e.target.value))
                  }
                >
                  {[5, 10, 20].map((num) => (
                    <option key={num} value={num}>
                      {num}
                    </option>
                  ))}
                </select>
                <CaretSortIcon className='absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none text-gray-500 w-4 h-4' />
              </div>
              <div className='ml-4 mr-4'>
                <span className='text-[#020617] text-[14px] font-medium'>{`Page ${currentPage} of ${totalPages}`}</span>
              </div>
              <div className='flex items-center gap-2'>
                <button
                  disabled={currentPage === 1 || templateList.length === 0}
                  className={`border p-1 pr-2 pl-2 rounded text-gray-500 ${currentPage === 1 || templateList.length === 0 ? 'cursor-not-allowed bg-gray-100' : 'hover:bg-gray-200'}`}
                  onClick={() => handlePageChange(1)}
                >
                  «
                </button>
                <button
                  disabled={currentPage === 1 || templateList.length === 0}
                  className={`border p-1 pr-2 pl-2 rounded text-gray-500 ${currentPage === 1 || templateList.length === 0 ? 'cursor-not-allowed bg-gray-100' : 'hover:bg-gray-200'}`}
                  onClick={() => handlePageChange(currentPage - 1)}
                >
                  ‹
                </button>
                <button
                  disabled={
                    currentPage === totalPages || templateList.length === 0
                  }
                  className={`border p-1 pr-2 pl-2 rounded text-gray-500 ${currentPage === totalPages || templateList.length === 0 ? 'cursor-not-allowed bg-gray-100' : 'hover:bg-gray-200'}`}
                  onClick={() => handlePageChange(currentPage + 1)}
                >
                  ›
                </button>
                <button
                  disabled={
                    currentPage === totalPages || templateList.length === 0
                  }
                  className={`border p-1 pr-2 pl-2 rounded text-gray-500 ${currentPage === totalPages || templateList.length === 0 ? 'cursor-not-allowed bg-gray-100' : 'hover:bg-gray-200'}`}
                  onClick={() => handlePageChange(totalPages)}
                >
                  »
                </button>
              </div>
            </div>
          </div>
        </div>

        <Dialog
          open={deleteConfirm.open}
          onOpenChange={(open) => setDeleteConfirm({ open, templateId: null })}
        >
          <DialogContent className='max-w-sm'>
            <DialogHeader>
              <DialogTitle>Confirm Delete</DialogTitle>
              <p className='text-sm text-gray-600'>
                Are you sure you want to delete this template? This action
                cannot be undone.
              </p>
            </DialogHeader>
            <div className='flex justify-end gap-3 mt-4'>
              <Button
                variant='outline'
                className='w-40'
                onClick={() =>
                  setDeleteConfirm({ open: false, templateId: null })
                }
              >
                Cancel
              </Button>
              <Button
                className='bg-red-600 hover:bg-red-700 text-white w-40'
                onClick={() =>
                  deleteConfirm.templateId &&
                  handleDelete(deleteConfirm.templateId)
                }
              >
                Delete
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent
            className={`${
              showDeletedTemplateMessage
                ? 'max-w-2xl'
                : 'max-w-6xl max-h-[90vh] overflow-y-auto'
            }`}
          >
            {!showDeletedTemplateMessage && (
              <DialogHeader>
                <DialogTitle className='text-2xl font-bold'>
                  {dialogMode === 'create'
                    ? 'Create Template'
                    : dialogMode === 'edit'
                      ? 'Edit Template'
                      : 'View Template'}
                </DialogTitle>
              </DialogHeader>
            )}

            {showDeletedTemplateMessage ? (
              <>
                <DialogHeader>
                  <DialogTitle className='sr-only'>
                    Template Deleted
                  </DialogTitle>
                </DialogHeader>
                <div className='flex flex-col items-center justify-center py-10'>
                  <div className='flex items-center justify-center w-16 h-16 rounded-full bg-gray-100'>
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      className='w-8 h-8 text-gray-400'
                      fill='none'
                      viewBox='0 0 24 24'
                      stroke='currentColor'
                      strokeWidth={1.5}
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        d='M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M9 7h6m2 0a2 2 0 00-2-2H9a2 2 0 00-2 2m10 0H5'
                      />
                    </svg>
                  </div>

                  <h2 className='text-2xl font-semibold mb-2'>
                    Template Deleted
                  </h2>

                  <p className='text-gray-500 text-center max-w-md'>
                    This welcome template was deleted and no more actions are
                    required.
                  </p>

                  {/* <Button
            variant="outline"
            className="w-[85px] border-blue-500 text-blue-500 hover:bg-blue-500 hover:text-white"
                    onClick={() => {
                      setIsDialogOpen(false);
                      setShowDeletedTemplateMessage(false);
                    }}
                  >
                    Back
                  </Button> */}
                </div>
              </>
            ) : (
              <CreateContentForm
                onClose={() => setIsDialogOpen(false)}
                onSave={handleSaveTemplate}
                initialContent={selectedTemplate}
                mode={dialogMode}
              />
            )}
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
};

export default AdminTemplateList;
// import React, { useState, useEffect } from "react";
// import { Button } from "../../../Components/ui/button";
// import { Input } from "../../../Components/ui/input";
// import { Badge } from "../../../Components/ui/badge";
// import { AlertTriangle } from "lucide-react";
// import CreateContentForm from "../AdminTemplates/CreateContentForm";
// import { toast } from "../../../Components/ui/use-toast";
// import {
//     CalendarIcon,
//     FileIcon,
//     CaretSortIcon,
//     DotsHorizontalIcon,
// } from "@radix-ui/react-icons";
// import { format } from "date-fns";
// import {
//     Popover,
//     PopoverContent,
//     PopoverTrigger,
// } from "../../../Components/ui/popover";
// import {
//     Table,
//     TableBody,
//     TableCell,
//     TableHead,
//     TableHeader,
//     TableRow,
// } from "../../../Components/ui/table";
// import { Calendar } from "../../../Components/ui/calendar";
// import {
//     DropdownMenu,
//     DropdownMenuContent,
//     DropdownMenuItem,
//     DropdownMenuTrigger,
// } from "../../../Components/ui/dropdown-menu";
// import { DateRange } from "react-day-picker";
// import { useSelector } from "react-redux";
// import { RootState } from "../../../State/store";
// import { useToast } from "../../../Components/ui/use-toast";
// import axios from 'axios';
// import { useNavigate } from "react-router-dom";
// import { AlertCircle } from 'lucide-react';
// import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../../Components/ui/tooltip"

// import {
//     Dialog,
//     DialogClose,
//     DialogContent,
//     DialogDescription,
//     DialogFooter,
//     DialogHeader,
//     DialogTrigger,
//     DialogTitle,
// } from "../../../Components/ui/dialog";

// interface Template {
//     template_Id: number;
//     templateName: string;
//     operator: string | null;
//     channelType: number;
//     templateStatus: string;
//     metaStatus?: string;
//     createdDate: string;
//     lastUpdatedDate: string;
//     partnerId: number | null;
// }

// const ChannelBadge: React.FC<{ channel: string }> = ({ channel }) => {
//     return (
//         <Badge
//             style={
//                 channel === "WhatsApp"
//                     ? { backgroundColor: "#479E98", color: "#fff" }
//                     : channel === "SMS"
//                         ? { backgroundColor: "#DFA548", color: "#fff" }
//                         : { backgroundColor: "#94A3B8", color: "#fff" }
//             }
//         >
//             {channel}
//         </Badge>
//     );
// };

// // const TemplateStatusBadge: React.FC<{ value?: string }> = ({ value }) => {
// //     const raw = (value ?? "").trim();
// //     const norm = raw.toLowerCase();

// //     const isApproved = norm === "live" || norm === "approved";

// //     return (
// //         <span
// //             className={
// //                 isApproved
// //                     ? "!bg-green-700 text-white hover:!bg-green-700 hover:text-white"
// //                     : "!bg-gray-400 text-white hover:!bg-gray-400 hover:text-white"
// //             }
// //             style={{
// //                 borderRadius: "9999px",
// //                 padding: "4px 14px",
// //                 fontSize: "12px",
// //                 fontWeight: 500,
// //                 display: "inline-block",
// //             }}
// //         >
// //             {isApproved ? "Approved" : "Pending"}
// //         </span>
// //     );
// // };
// // const TemplateStatusBadge: React.FC<{ value?: string }> = ({ value }) => {
// //     const raw = (value ?? "").trim();
// //     const norm = raw.toLowerCase();

// //     const isApproved = norm === "live" || norm === "approved";

// //     return (
// //         <span
// //             className={
// //                 isApproved
// //                     ? "!bg-green-700 text-white hover:!bg-green-700 hover:text-white"
// //                     : "!bg-gray-400 text-white hover:!bg-gray-400 hover:text-white"
// //             }
// //             style={{
// //                 borderRadius: "9999px",
// //                 padding: "4px 14px",
// //                 fontSize: "12px",
// //                 fontWeight: 500,
// //                 display: "inline-block",
// //             }}
// //         >
// //             {isApproved ? "Approved" : "Pending"}
// //         </span>
// //     );
// // };

// const TemplateStatusBadge: React.FC<{ value?: string }> = ({ value }) => {
//     const raw = (value ?? "").trim();
//     const norm = raw.toLowerCase();

//     const isApproved = norm === "live" || norm === "approved";
//     const isRejected = norm === "rejected";

//     return (
//         <span
//             className={
//                 isApproved
//                     ? "!bg-green-700 text-white hover:!bg-green-700 hover:text-white"
//                     : isRejected
//                         ? "!bg-red-600 text-white hover:!bg-red-600 hover:text-white"
//                         : "!bg-gray-400 text-white hover:!bg-gray-400 hover:text-white"
//             }
//             style={{
//                 borderRadius: "9999px",
//                 padding: "4px 14px",
//                 fontSize: "12px",
//                 fontWeight: 500,
//                 display: "inline-block",
//             }}
//         >
//             {isApproved ? "Approved" : isRejected ? "Rejected" : "Pending"}
//         </span>
//     );
// };

// const DatePickerWithRange: React.FC<{
//     dateRange: DateRange | undefined;
//     setDateRange: (range: DateRange | undefined) => void;
// }> = ({ dateRange, setDateRange }) => {
//     return (
//         <Popover>
//             <PopoverTrigger asChild>
//                 <Button id="date" variant="outline" className={`w-[260px] justify-start text-left font-normal !mt-0 ${!dateRange ? "text-muted-foreground" : ""}`}>
//                     <CalendarIcon className="mr-2 h-4 w-4" />
//                     {dateRange?.from ? (dateRange.to ? <>{format(dateRange.from, "LLL dd, y")} - {format(dateRange.to, "LLL dd, y")}</> : format(dateRange.from, "LLL dd, y")) : <span>Pick a date range</span>}
//                 </Button>
//             </PopoverTrigger>
//             <PopoverContent className="w-auto p-0" align="start">
//                 <Calendar initialFocus mode="range" defaultMonth={dateRange?.from} selected={dateRange} onSelect={setDateRange} numberOfMonths={2} />
//             </PopoverContent>
//         </Popover>
//     );
// };

// const AdminTemplateList: React.FC = () => {
//     const [dateRange, setDateRange] = useState<DateRange | undefined>();
//     const [templateList, setTemplateList] = useState<Template[]>([]);
//     const [loading, setLoading] = useState(false);
//     const [openMenuRowId, setOpenMenuRowId] = useState<number | null>(null);
//     const [checkboxSelectedRows, setCheckboxSelectedRows] = useState<number[]>([]);
//     const [isAllSelected, setIsAllSelected] = useState(false);
//     const [currentPage, setCurrentPage] = useState<number>(1);
//     const [rowsPerPage, setRowsPerPage] = useState<number>(10);
//     const [searchTerm, setSearchTerm] = useState("");
//     const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
//     const [totalCount, setTotalCount] = useState<number>(0);
//     const [openDialog, setOpenDialog] = useState(false);
//     const adminUrl = useSelector((state: RootState) => state.authentication.adminUrl);
//     const [selectedTemplate, setSelectedTemplate] = useState<any>(null);
//     const [dialogMode, setDialogMode] = useState<'create' | 'edit' | 'view'>('create');
//     const [isDialogOpen, setIsDialogOpen] = useState(false);
// const toast = useToast();

// // Debounce search term
// useEffect(() => {
//     const handler = setTimeout(() => {
//         setDebouncedSearchTerm(searchTerm);
//     }, 500);
//     return () => clearTimeout(handler);
// }, [searchTerm]);

// useEffect(() => {
//     fetchTemplates();
// }, [currentPage, rowsPerPage, debouncedSearchTerm, dateRange]);

//     const fetchTemplates = async () => {
//         setLoading(true);
//         try {
//             const params: any = {
//                 page: currentPage,
//                 rowsPerPage: rowsPerPage,
//             };

//             if (debouncedSearchTerm) params.searchTerm = debouncedSearchTerm;
//             if (dateRange?.from) params.startDate = format(dateRange.from, "yyyy-MM-dd");
//             if (dateRange?.to) params.endDate = format(dateRange.to, "yyyy-MM-dd");
//             const response = await axios.get(`${adminUrl}/GetTemplatesByChannel`, { params });

//             if (response.data.status === "Success") {  // Changed from Status to status
//                 setTemplateList(response.data.templateList || []);  // Changed from TemplateList to templateList
//                 setTotalCount(response.data.distinctTemplateCount || 0);  // Changed from DistinctTemplateCount to distinctTemplateCount
//             } else {
//                 setTemplateList([]);
//                 setTotalCount(0);
//             }
//         } catch (error) {
//             console.error("Error fetching templates:", error);
//             setTemplateList([]);
//             setTotalCount(0);
//         } finally {
//             setLoading(false);
//         }
//     };

//     const getChannelName = (channelType: number): string => {
//         switch (channelType) {
//             case 1: return "WhatsApp";
//             case 2: return "SMS";
//             default: return "Unknown";
//         }
//     };

//     const handleCheckboxRowSelect = (id: number) => {
//         setCheckboxSelectedRows((prev) => {
//             const newSelectedRows = prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id];
//             setIsAllSelected(newSelectedRows.length === templateList.length);
//             return newSelectedRows;
//         });
//     };

//     const handleSelectAll = () => {
//         if (isAllSelected) {
//             setCheckboxSelectedRows([]);
//         } else {
//             const allIds = templateList.map((_, index) => index);
//             setCheckboxSelectedRows(allIds);
//         }
//         setIsAllSelected(!isAllSelected);
//     };

//     const handlePageChange = (newPage: number) => {
//         if (newPage > 0 && newPage <= totalPages) {
//             setCurrentPage(newPage);
//         }
//     };

//     const handleExportButtonClick = () => {
//         console.log("Export clicked");
//     };

//     const handleSaveTemplate = async () => {
//         try {
//             await fetchTemplates();      // refresh list
//             setOpenDialog(false);        // close modal

//             toast.toast({
//                 title: "Success",
//                 description: "Template submitted successfully",
//             });

//         } catch (error) {
//             console.error("Refresh failed:", error);

//             toast.toast({
//                 title: "Error",
//                 description: "Template saved but failed to refresh list",
//                 variant: "destructive",
//             });
//         }
//     };

//     const handleView = async (template_Id: number) => {
//         console.log("Template ID:", template_Id);

//         if (!template_Id) {
//             console.error("No template ID provided");
//             toast.toast({
//                 title: "Error",
//                 description: "Template ID not found",
//                 variant: "destructive"
//             });
//             return;
//         }

//         setIsDialogOpen(true);
//         setDialogMode('view');
//         setSelectedTemplate(null); // Clear previous data

//         try {
//             const response = await axios.get(
//                 `${adminUrl}/GetTemplateById?templateId=${template_Id}`
//             );

//             if (response.data) {
//                 setSelectedTemplate(response.data);
//             } else {
//                 toast.toast({
//                     title: "Error",
//                     description: "Failed to fetch template",
//                     variant: "destructive"
//                 });
//                 setIsDialogOpen(false);
//             }
//         } catch (error) {
//             console.error("Error fetching template:", error);
//             toast.toast({
//                 title: "Error",
//                 description: "Failed to fetch template details",
//                 variant: "destructive"
//             });
//             setIsDialogOpen(false);
//         }
//     };

//     const handleEdit = async (template_Id: number) => {
//         if (!template_Id) {
//             toast.toast({
//                 title: "Error",
//                 description: "Template ID not found",
//                 variant: "destructive"
//             });
//             return;
//         }

//         setIsDialogOpen(true);
//         setDialogMode('edit');
//         setSelectedTemplate(null); // Clear previous

//         try {
//             const response = await axios.get(
//                 `${adminUrl}/GetTemplateById?templateId=${template_Id}`
//             );

//             if (response.data) {
//                 setSelectedTemplate(response.data);
//             } else {
//                 toast.toast({
//                     title: "Error",
//                     description: "Failed to fetch template",
//                     variant: "destructive"
//                 });
//                 setIsDialogOpen(false);
//             }
//         } catch (error) {
//             console.error("Error fetching template for edit:", error);
//             toast.toast({
//                 title: "Error",
//                 description: "Failed to fetch template details",
//                 variant: "destructive"
//             });
//             setIsDialogOpen(false);
//         }
//     };

//     const totalPages = Math.ceil(totalCount / rowsPerPage);

//     const navigate = useNavigate();

//     debugger;

//     const handleTemplateStatusClick = (
//         templateId: number,
//         channelType: number
//     ) => {
//         navigate("/adminNavbar/templatestatus", {
//             state: {
//                 campaignId: templateId,
//                 channel_type: channelType,   // ✅ send number directly
//             }
//         });
//     };

//     const getTemplateStatus = (template: Template) => {
//         const templateStatus = template.templateStatus.toLowerCase();
//         const channelType = template.channelType;

//         // Check for rejection (any channel)
//         if (templateStatus.includes('rejected')) {
//             return { status: 'Rejected', showAction: true, variant: 'rejected' };
//         }

//         // WhatsApp (channelType = 1): Show "Approved" if status is "Whatsapp Approved"
//         if (channelType === 1 && templateStatus === 'whatsapp approved') {
//             return { status: 'Approved', showAction: false, variant: 'approved' };
//         }

//         // SMS (channelType = 2): Show "Approved" if status is "Operator Approved"
//         if (channelType === 2 && templateStatus === 'operator approved') {
//             return { status: 'Approved', showAction: false, variant: 'approved' };
//         }

//         // Default: Show the original status as Pending
//         return { status: 'Pending', showAction: false, variant: 'pending' };
//     };

//     return (
//         <TooltipProvider>
//             <div>
//                 <div className="fixed flex justify-end items-end right-0 top-[-24px] z-20 p-4 mr-2">
//                     <Button
//                         className="w-30 text-sm text-[#F8FAFC] font-medium h-[35px]"
//                         onClick={() => {
//                             setDialogMode("create");
//                             setSelectedTemplate(null);
//                             setIsDialogOpen(true);
//                         }}
//                     >
//                         Create Template
//                     </Button>

//                 </div>

//                 <div className="mt-4">
//                     <div className="flex flex-wrap items-center justify-between gap-4 -mt-6">
//                         <div className="flex flex-wrap justify-start w-full md:w-auto">
//                             <Input
//                                 placeholder="Search template by name..."
//                                 className="w-full md:w-[350px] text-[14px] font-normal text-[#171717] !mt-0"
//                                 value={searchTerm}
//                                 onChange={(e) => {
//                                     setSearchTerm(e.target.value);
//                                     setCurrentPage(1);
//                                 }}
//                             />
//                         </div>
//                         <div className="flex flex-wrap items-center justify-end gap-10 w-full md:w-auto">
//                             <div className="flex-shrink-0 -mr-5">
//                                 <DatePickerWithRange dateRange={dateRange} setDateRange={setDateRange} />
//                             </div>
//                             <div className="flex-shrink-0">
//                                 <Button variant="outline" className="w-24 text-[#020617] !mt-0" onClick={handleExportButtonClick}>
//                                     <FileIcon style={{ width: '14px', height: '14px' }} className="mr-1 font-bold stroke-[2]" /> Export
//                                 </Button>
//                             </div>
//                         </div>
//                     </div>

//                     <div className="rounded-md border mt-8">
//                         <div className="max-h-[60vh] overflow-y-auto">
//                             <Table className="rounded-xl whitespace-nowrap border-gray-200" style={{ color: "#020202", fontSize: "15px" }}>
//                                 <TableHeader>
//                                     <TableRow className="sticky top-0 z-10 bg-white hover:bg-white">
//                                         <TableHead className="pl-6 pr-3 py-2 font-medium align-middle">
//                                             <div className="flex items-center gap-3">
//                                                 <input type="checkbox" className="accent-gray-700" checked={isAllSelected} onChange={handleSelectAll} />
//                                                 <span>Template Name</span>
//                                             </div>
//                                         </TableHead>
//                                         <TableHead className="py-2 px-3 font-medium align-middle">Operator</TableHead>
//                                         <TableHead className="py-2 px-3 font-medium align-middle">Channel</TableHead>
//                                         <TableHead className="py-2 px-3 font-medium align-middle">Template Status</TableHead>
//                                         <TableHead className="py-2 px-3 font-medium align-middle">Created Date</TableHead>
//                                         <TableHead className="py-2 px-3 font-medium align-middle">Last Modified</TableHead>
//                                         <TableHead className="w-12 py-2 align-middle"></TableHead>
//                                     </TableRow>
//                                 </TableHeader>
//                                 <TableBody>
//                                     {loading ? (
//                                         <TableRow><TableCell colSpan={7} className="text-center py-2 align-middle">Loading templates...</TableCell></TableRow>
//                                     ) : templateList.length > 0 ? (
//                                         templateList.map((template, index) => {
//                                             const isSelected = checkboxSelectedRows.includes(index);

//                                             // Calculate status info and edit permission for this template
//                                             const statusInfo = getTemplateStatus(template);
//                                             const canEdit = template.templateStatus.toLowerCase().includes('rejected');

//                                             return (
//                                                 <TableRow key={index} className={isSelected ? "bg-gray-200" : ""}>
//                                                     <TableCell className="pl-6 pr-3 py-2 align-middle">
//                                                         <div className="flex items-center gap-3">
//                                                             <input type="checkbox" className="accent-gray-700" checked={isSelected} onChange={() => handleCheckboxRowSelect(index)} />
//                                                             <span>{template.templateName}</span>
//                                                         </div>
//                                                     </TableCell>
//                                                     <TableCell className="py-2 px-3 align-middle text-left">{template.operator || "N/A"}</TableCell>
//                                                     <TableCell className="py-2 px-3 align-middle text-left"><ChannelBadge channel={getChannelName(template.channelType)} /></TableCell>
//                                                     <TableCell
//                                                         className="py-2 px-3 align-middle text-left cursor-pointer hover:bg-gray-50"
//                                                         onClick={() =>
//                                                             handleTemplateStatusClick(
//                                                                 template.template_Id,
//                                                                 template.channelType
//                                                             )
//                                                         }
//                                                     >
//                                                         <div className="flex items-center gap-2">
//                                                             <TemplateStatusBadge value={statusInfo.status} />
//                                                             {statusInfo.showAction && (
//                                                                 <Tooltip>
//                                                                     <TooltipTrigger>
//                                                                         <AlertTriangle
//                                                                             size={20}
//                                                                             className="text-red-600 transform-gpu transition-transform duration-200 ease-out hover:scale-[1.08]"
//                                                                             aria-hidden="true"
//                                                                         />
//                                                                     </TooltipTrigger>
//                                                                     <TooltipContent className="bg-black text-white border-black">
//                                                                         <p>Action required: Template rejected</p>
//                                                                     </TooltipContent>
//                                                                 </Tooltip>
//                                                             )}
//                                                         </div>
//                                                     </TableCell>
//                                                     <TableCell className="py-2 px-3 align-middle text-left">{template.createdDate ? format(new Date(template.createdDate), "dd/MM/yyyy") : "N/A"}</TableCell>
//                                                     <TableCell className="py-2 px-3 align-middle text-left">{template.lastUpdatedDate ? format(new Date(template.lastUpdatedDate), "dd/MM/yyyy") : "N/A"}</TableCell>
//                                                     <TableCell className="py-2 pr-3 align-middle">
//                                                         <DropdownMenu open={openMenuRowId === index} onOpenChange={(open) => setOpenMenuRowId(open ? index : null)}>
//                                                             <DropdownMenuTrigger asChild><Button variant="ghost" className="h-8 w-8 p-0"><DotsHorizontalIcon className="h-4 w-4" /></Button></DropdownMenuTrigger>
//                                                             {openMenuRowId === index && (
//                                                                 <DropdownMenuContent align="end" className="w-40 bg-white shadow-lg rounded-md">
//                                                                     {canEdit && (
//                                                                         <DropdownMenuItem onClick={() => handleEdit(template.template_Id)}>
//                                                                             Edit
//                                                                         </DropdownMenuItem>
//                                                                     )}
//                                                                     <DropdownMenuItem
//                                                                         onSelect={() => {
//                                                                             console.log("Template object:", template);
//                                                                             console.log("Template ID:", template.template_Id);
//                                                                             handleView(template.template_Id);
//                                                                         }}
//                                                                     >
//                                                                         View
//                                                                     </DropdownMenuItem>

//                                                                     <DropdownMenuItem onClick={() => setOpenMenuRowId(null)} className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-red-600">Delete</DropdownMenuItem>
//                                                                 </DropdownMenuContent>
//                                                             )}
//                                                         </DropdownMenu>
//                                                     </TableCell>
//                                                 </TableRow>
//                                             );
//                                         })
//                                     ) : (
//                                         <TableRow><TableCell colSpan={7} className="text-center py-2 text-gray-500 font-medium align-middle">{searchTerm.trim() !== "" ? "No templates found for the searched item." : "No templates available."}</TableCell></TableRow>
//                                     )}
//                                 </TableBody>
//                             </Table>
//                         </div>
//                     </div>

//                     <div className="flex justify-between items-center mt-4">
//                         <div className="flex items-center space-x-2 text-gray-500 text-sm">
//                             <span>{`${(currentPage - 1) * rowsPerPage + 1}-${Math.min(currentPage * rowsPerPage, totalCount)} of ${totalCount} row(s) selected`}</span>
//                         </div>
//                         <div className="flex items-center space-x-4 font-medium text-sm">
//                             <span className="text-[#020617] font-medium text-[14px]">Rows per page</span>
//                             <div className="relative inline-block ml-2">
//                                 <select
//                                     disabled={templateList.length === 0}
//                                     className={`cursor-pointer border border-gray-300 rounded-md px-2 py-1 appearance-none focus:outline-none focus:ring-1 focus:ring-blue-500 ${templateList.length === 0 ? "cursor-not-allowed bg-gray-100" : ""}`}
//                                     style={{ width: "60px", height: "30px", textAlign: "left", fontSize: "12px", fontWeight: "400", borderColor: "#E5E7EB", boxShadow: "inset 0 1px 2px rgba(0, 0, 0, 0.1)" }}
//                                     value={templateList.length === 0 ? 0 : rowsPerPage}
//                                     onChange={(e) => {
//                                         setRowsPerPage(Number(e.target.value));
//                                         setCurrentPage(1);
//                                     }}
//                                 >
//                                     {[5, 10, 20].map((num) => (<option key={num} value={num}>{num}</option>))}
//                                 </select>
//                                 <CaretSortIcon className="absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none text-gray-500 w-4 h-4" />
//                             </div>
//                             <div className="ml-4 mr-4">
//                                 <span className="text-[#020617] text-[14px] font-medium">{`Page ${currentPage} of ${totalPages}`}</span>
//                             </div>
//                             <div className="flex items-center gap-2">
//                                 <button disabled={currentPage === 1 || templateList.length === 0} className={`border p-1 pr-2 pl-2 rounded text-gray-500 ${currentPage === 1 || templateList.length === 0 ? "cursor-not-allowed bg-gray-100" : "hover:bg-gray-200"}`} onClick={() => handlePageChange(1)}>«</button>
//                                 <button disabled={currentPage === 1 || templateList.length === 0} className={`border p-1 pr-2 pl-2 rounded text-gray-500 ${currentPage === 1 || templateList.length === 0 ? "cursor-not-allowed bg-gray-100" : "hover:bg-gray-200"}`} onClick={() => handlePageChange(currentPage - 1)}>‹</button>
//                                 <button disabled={currentPage === totalPages || templateList.length === 0} className={`border p-1 pr-2 pl-2 rounded text-gray-500 ${currentPage === totalPages || templateList.length === 0 ? "cursor-not-allowed bg-gray-100" : "hover:bg-gray-200"}`} onClick={() => handlePageChange(currentPage + 1)}>›</button>
//                                 <button disabled={currentPage === totalPages || templateList.length === 0} className={`border p-1 pr-2 pl-2 rounded text-gray-500 ${currentPage === totalPages || templateList.length === 0 ? "cursor-not-allowed bg-gray-100" : "hover:bg-gray-200"}`} onClick={() => handlePageChange(totalPages)}>»</button>
//                             </div>
//                         </div>
//                     </div>
//                 </div>

//                 <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
//                     <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
//                         <DialogHeader>
//                             <DialogTitle className="text-2xl font-bold">
//                                 {dialogMode === 'create' ? 'Create Template' : dialogMode === 'edit' ? 'Edit Template' : 'View Template'}
//                             </DialogTitle>
//                         </DialogHeader>
//                         <CreateContentForm
//                             onClose={() => setIsDialogOpen(false)}
//                             onSave={handleSaveTemplate}
//                             initialContent={selectedTemplate}
//                             mode={dialogMode}
//                         />
//                     </DialogContent>
//                 </Dialog>
//             </div>
//         </TooltipProvider>
//     );
// };

// export default AdminTemplateList;
